import { useQuery } from '@apollo/client/react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Plus } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { Offers_Status_Enum } from '@graphql/__generated__/graphql';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { type Currency, getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

export const GET_BUYER_LEAD_OFFER = gql(/* GraphQL */ `
  query GetBuyerLeadOffer($buyerId: uuid!, $lotId: uuid!) {
    offers(
      where: { buyer_id: { _eq: $buyerId }, lot_id: { _eq: $lotId } }
      order_by: { created_at: desc }
      limit: 1
    ) {
      id
      amount
      status
      created_at
      lot {
        id
        currency
      }
    }
  }
`);

const STATUS_COLOR: Record<
  Offers_Status_Enum,
  'success' | 'error' | 'warning'
> = {
  [Offers_Status_Enum.Accepted]: 'success',
  [Offers_Status_Enum.Refused]: 'error',
  [Offers_Status_Enum.Pending]: 'warning',
};

const statusLabel = (status: Offers_Status_Enum, t: Translate) => {
  switch (status) {
    case Offers_Status_Enum.Accepted:
      return t('Accepted');
    case Offers_Status_Enum.Refused:
      return t('Refused');
    case Offers_Status_Enum.Pending:
      return t('Pending');
  }
};

export const BuyerLeadOfferCard: React.FC<{
  lotId: string;
  buyerId: string;
  // When true (listing context), the "Create offer" button opens the offer
  // drawer as an overlay over the current tab instead of navigating to the
  // listing's Offers tab (which would unmount this drawer).
  createInOverlay?: boolean;
  readOnly?: boolean;
}> = ({ lotId, buyerId, createInOverlay = false, readOnly = false }) => {
  const { t, locale, countryCode } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error } = useQuery(GET_BUYER_LEAD_OFFER, {
    variables: { buyerId, lotId },
  });

  const openCreateOverlay = () => {
    const next = new URLSearchParams(searchParams);
    next.set('create_offer', '1');
    next.set('buyerId', buyerId);
    setSearchParams(next);
  };

  const offer = data?.offers[0];
  const currency = (offer?.lot?.currency ??
    getCurrencyByCountryCode(countryCode)) as Currency;

  return (
    <ListingAccordion title={t('Offer')} defaultExpanded loading={loading}>
      {error != null ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('Could not load offer')}
        </Typography>
      ) : offer != null ? (
        <Stack
          component={Link}
          to={`/listings/${lotId}/offers/${offer.id}`}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatPrice(offer.amount, locale, currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              <TimeAgo dateString={offer.created_at} addSuffix />
            </Typography>
          </Box>
          <Chip
            size="small"
            label={statusLabel(offer.status, t)}
            color={STATUS_COLOR[offer.status]}
            variant="outlined"
          />
        </Stack>
      ) : (
        <Button
          variant="contained"
          startIcon={<Plus />}
          fullWidth
          disabled={readOnly}
          {...(createInOverlay
            ? { onClick: openCreateOverlay }
            : {
                component: Link,
                to: `/listings/${lotId}/offers/new?buyerId=${buyerId}`,
              })}
        >
          {t('Create offer')}
        </Button>
      )}
    </ListingAccordion>
  );
};
